/**
 * PersonneAddForm and its NameFormChips helper for the Cibles personne category.
 */
import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import {
  TARGET_LIMITS,
  PERSON_TITLES,
  PERSON_TITLE_LANG_LABELS,
  generatePersonNames,
  getPersonTitleDef,
} from '@traaker/shared';
import { type TargetRow, type TagRegistry, tagNamesOf, CAT_BY_ID } from './CATEGORIES';
import { ChipListEditor, ParentValue } from './CatalogueViews';
import {
  K, microLabel, DialogHead, DialogFoot, FieldLabel, FieldHint, dialogPaperProps, dialogContentSx,
  inputSx, selectSx, dropdownSx, primaryBtnSx, secondaryBtnSx,
} from './ui';

// ---------------------------------------------------------------------------
// NameFormChips — shared by PersonneAddForm sections
// ---------------------------------------------------------------------------

/** Renders a row of clickable name-form chips. Shared by all three sections. */
export function NameFormChips({
  forms, activeNames, onToggle, color,
}: {
  forms: import('@traaker/shared').PersonNameForm[];
  activeNames: Set<string>;
  onToggle: (form: string, core?: string) => void;
  color: string;
}) {
  if (!forms.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.625 }}>
      {forms.map(({ form, core }) => {
        const on = activeNames.has(form);
        return (
          <Box
            key={form}
            onClick={() => onToggle(form, core)}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.625,
              px: 1.125, height: 28, borderRadius: '8px', cursor: 'pointer',
              border: '1px solid',
              borderColor: on ? alpha(color, 0.45) : K.line,
              bgcolor: on ? alpha(color, 0.08) : K.paper,
              color: on ? color : K.ink2,
              fontSize: 12.5, fontWeight: on ? 600 : 500,
              userSelect: 'none', transition: 'all 0.1s',
              '&:hover': { borderColor: color, color },
            }}
          >
            {on && (
              <Box component="span" sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: color, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1.5 5.2l2.5 2.5 4.5-5"/></svg>
              </Box>
            )}
            {form}
          </Box>
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// PersonneAddForm
// ---------------------------------------------------------------------------

/**
 * Builds the accepted name combinations for a person and lets the user toggle each one.
 * Checked entries become `alias` field values that compile to `text` signals.
 * `title`, `firstName`, `lastName` are stored as building-block fields (no direct signal).
 */
export function PersonneAddForm({
  open, mode = 'add', initialRow, marques, onAdd, onClose, saving, tagRegistry, workingLang,
}: {
  open: boolean;
  mode?: 'add' | 'edit';
  initialRow?: TargetRow;
  marques: TargetRow[];
  onAdd: (data: { label: string; parentTargetId?: string; fields: { name: string; value: any }[]; tags?: string[] }) => void;
  onClose: () => void;
  saving: boolean;
  tagRegistry: TagRegistry;
  /** Brand working language — used to search titles and display language label in title picker. */
  workingLang: string;
}) {
  const { t } = useTranslation();
  const [parentId, setParentId] = useState('');
  const [titleCode, setTitleCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [titleSearch, setTitleSearch] = useState('');
  const [titleDropOpen, setTitleDropOpen] = useState(false);
  const titleSearchRef = useRef<HTMLInputElement>(null);

  /** Set of form strings that are ACTIVE (will become alias signals). */
  const [activeNames, setActiveNames] = useState<Set<string>>(new Set());

  const isEdit = mode === 'edit';

  // Tracks whether the name-change effect has seen its first value after the form opened.
  // False → first run after open = just record, don't reset (respects pre-filled edit values).
  // True  → subsequent run = user actually changed the name → full reset.
  const nameInitializedRef = useRef(false);

  // Pre-fill on open
  useEffect(() => {
    if (!open) return;
    nameInitializedRef.current = false; // arm the "skip first run" guard
    if (isEdit && initialRow) {
      setParentId(initialRow.parentTargetId ?? '');
      setTitleCode(initialRow.fields.find((f) => f.name === 'title')?.value as string ?? '');
      setFirstName(initialRow.fields.find((f) => f.name === 'firstName')?.value as string ?? '');
      setLastName(initialRow.fields.find((f) => f.name === 'lastName')?.value as string ?? '');
      const existingAliases = new Set(
        initialRow.fields.filter((f) => f.name === 'alias').map((f) => String(f.value)).filter(Boolean)
      );
      setActiveNames(existingAliases);
      setTags(tagNamesOf(initialRow, tagRegistry));
    } else {
      setParentId(marques.length === 1 ? marques[0].targetId : '');
      setTitleCode('');
      setFirstName('');
      setLastName('');
      setActiveNames(new Set());
      setTags([]);
    }
    setTitleSearch('');
    setTitleDropOpen(false);
  }, [open]);

  // Regenerate names when title/firstName/lastName change.
  const titleDef = getPersonTitleDef(titleCode) ?? null;
  const allForms = generatePersonNames(titleDef, firstName, lastName);

  // Sections for display
  const sansTitreForms  = allForms.filter((f) => f.lang === '');
  const workingLangForms = allForms.filter((f) => f.lang === workingLang);
  const otherLangForms   = allForms.filter((f) => f.lang !== '' && f.lang !== workingLang);

  // Collapse state for the two optional sections
  const [showTitleForms, setShowTitleForms] = useState(false);
  const [showAllLangForms, setShowAllLangForms] = useState(false);

  // When title CHANGES, remove ALL titled forms (every language of the previous title)
  // so they don't accumulate. Sans-titre forms (the name itself) are kept — the person
  // hasn't changed, only the way they're cited with a title.
  const prevTitleCodeRef = useRef(titleCode);
  useEffect(() => {
    const prev = prevTitleCodeRef.current;
    prevTitleCodeRef.current = titleCode;
    if (prev === titleCode) return;
    // Remove any form that was produced by ANY title (not just the previous one) — simpler
    // and future-proof: keep only the forms that are in the current generation's lang='' group.
    setActiveNames((p) => {
      const sansTitre = new Set(
        generatePersonNames(null, firstName, lastName).map((f) => f.form),
      );
      const next = new Set<string>();
      for (const name of p) {
        if (sansTitre.has(name)) next.add(name);
      }
      return next;
    });
    setShowTitleForms(false);
    setShowAllLangForms(false);
  }, [titleCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // When firstName or lastName changes: full reset to default sans-titre forms.
  // First run after open is skipped (nameInitializedRef guard) so pre-filled edit
  // values are respected. Every subsequent change is a real user edit → reboot.
  const prevSansTitreRef = useRef<string>('');
  useEffect(() => {
    const key = sansTitreForms.map((f) => f.form).join('|');
    if (key === prevSansTitreRef.current) return;
    const wasInitialized = nameInitializedRef.current;
    nameInitializedRef.current = true;
    prevSansTitreRef.current = key;
    if (!wasInitialized) return; // first run after open — don't reset
    // Name changed → reboot: clear everything and set only the defaultOn sans-titre forms.
    setActiveNames(new Set(sansTitreForms.filter((f) => f.defaultOn).map((f) => f.form)));
    setShowTitleForms(false);
    setShowAllLangForms(false);
  }, [sansTitreForms.map((f) => f.form).join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Toggle a form. When checking a titled form, auto-uncheck its `core` (the
   *  corresponding no-title form that becomes redundant). */
  const toggleName = (form: string, core?: string) => {
    setActiveNames((prev) => {
      const next = new Set(prev);
      if (next.has(form)) {
        next.delete(form);
      } else {
        next.add(form);
        if (core) next.delete(core); // uncheck the less-specific form it supersedes
      }
      return next;
    });
  };

  // Title search: match against all forms of every title in the working language (+ EN fallback)
  const searchLangs = [workingLang, 'en', 'fr'].filter((v, i, a) => a.indexOf(v) === i);
  const titleMatches = titleSearch.trim().length < 1
    ? PERSON_TITLES
    : PERSON_TITLES.filter((td) => {
        const q = titleSearch.trim().toLowerCase();
        for (const lang of searchLangs) {
          const lf = td.forms[lang];
          if (!lf) continue;
          for (const f of [...lf.short, ...(lf.long ?? [])]) {
            if (f.toLowerCase().includes(q)) return true;
          }
        }
        // Also match code and emoji
        return td.code.toLowerCase().includes(q);
      });

  const buildLabel = (): string => {
    const parts = [titleCode ? `${titleCode} ` : '', firstName ? `${firstName} ` : '', lastName].join('').trim();
    return parts || t('cibles.unnamed');
  };

  const handleSave = () => {
    if (!lastName.trim()) return;
    const fields: { name: string; value: string }[] = [];
    if (titleCode) fields.push({ name: 'title', value: titleCode });
    if (firstName.trim()) fields.push({ name: 'firstName', value: firstName.trim() });
    fields.push({ name: 'lastName', value: lastName.trim() });
    for (const form of activeNames) {
      fields.push({ name: 'alias', value: form });
    }
    onAdd({ label: buildLabel(), parentTargetId: parentId || undefined, fields, tags });
  };

  const cat = CAT_BY_ID['personne'];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
      <DialogHead
        title={isEdit ? t('cibles.personne.edit_title') : t('cibles.add.personne')}
        subtitle={t('cibles.personne.subtitle')}
        onClose={onClose}
        disabled={saving}
      />

      <DialogContent sx={dialogContentSx}>

        {/* Brand (optional) */}
        <Box>
          <FieldLabel optional>{t('cibles.parent_column')}</FieldLabel>
          <Select size="small" value={parentId} onChange={(e) => setParentId(e.target.value as string)} displayEmpty fullWidth
            renderValue={(value) => <ParentValue marques={marques} value={value as string} />}
            sx={selectSx}>
            <MenuItem value="" sx={{ fontSize: 13, color: K.ink3 }}>{t('cibles.parent_none')}</MenuItem>
            {marques.map((m) => (
              <MenuItem key={m.targetId} value={m.targetId} sx={{ fontSize: 13 }}>{m.label}</MenuItem>
            ))}
          </Select>
        </Box>

        {/* Title + First name + Last name */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr', gap: 1.5 }}>
          {/* Title — searchable picker */}
          <Box>
            <FieldLabel optional>{t('cibles.personne.title_label')}</FieldLabel>
            <Box sx={{ position: 'relative' }}>
              <Box
                onClick={() => { setTitleDropOpen((p) => !p); setTimeout(() => titleSearchRef.current?.focus(), 50); }}
                sx={{
                  height: 40, px: 1.25, border: '1px solid', borderColor: titleDropOpen ? K.primary : K.line,
                  borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
                  bgcolor: K.paper,
                  boxShadow: titleDropOpen ? K.focusRing : 'none',
                  '&:hover': { borderColor: titleDropOpen ? K.primary : 'rgba(0,0,0,.18)' },
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
              >
                {titleDef ? (
                  <>
                    <Typography component="span" sx={{ fontSize: '1rem', lineHeight: 1, flexShrink: 0 }}>{titleDef.emoji}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: K.ink, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {titleDef.forms[workingLang]?.short[0] ?? titleDef.forms['en']?.short[0] ?? titleDef.code}
                    </Typography>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setTitleCode(''); setTitleSearch(''); }}
                      sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}>
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </>
                ) : (
                  <Typography sx={{ fontSize: 13, color: K.ink3, flex: 1 }}>
                    {t('cibles.personne.title_none')}
                  </Typography>
                )}
              </Box>
              {titleDropOpen && (
                <Box sx={{ ...dropdownSx, position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 'auto', minWidth: 260, zIndex: 600 }}>
                  <Box sx={{ px: 1.25, py: 0.875, borderBottom: `1px solid ${K.line}`, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <SearchIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
                    <Box
                      component="input"
                      ref={titleSearchRef}
                      value={titleSearch}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitleSearch(e.target.value)}
                      placeholder={t('cibles.personne.title_search')}
                      sx={{ border: 'none', outline: 'none', flex: 1, fontSize: '0.8125rem', fontFamily: 'inherit', bgcolor: 'transparent', color: 'text.primary', '&::placeholder': { color: 'text.disabled' } }}
                    />
                  </Box>
                  <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                    {titleMatches.length === 0 ? (
                      <Typography sx={{ px: 1.5, py: 1, fontSize: '0.8125rem', color: 'text.disabled' }}>{t('cibles.personne.title_no_result')}</Typography>
                    ) : titleMatches.map((td) => {
                      const langForms = td.forms[workingLang] ?? td.forms['en'] ?? td.forms[Object.keys(td.forms)[0]];
                      const displayLabel = langForms ? [...langForms.short, ...(langForms.long ?? [])].slice(0, 2).join(' / ') : td.code;
                      return (
                        <Box key={td.code}
                          onClick={() => { setTitleCode(td.code); setTitleDropOpen(false); setTitleSearch(''); }}
                          sx={{ px: 1.5, py: 0.875, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${K.line}`,
                            bgcolor: td.code === titleCode ? K.primarySoft : 'transparent', '&:hover': { bgcolor: td.code === titleCode ? K.primarySoft : K.rowHover }, '&:last-child': { borderBottom: 'none' } }}>
                          <Typography component="span" sx={{ fontSize: '1rem', lineHeight: 1, width: 22, flexShrink: 0 }}>{td.emoji}</Typography>
                          <Typography sx={{ fontSize: '0.8125rem', fontWeight: td.code === titleCode ? 600 : 400, flex: 1, whiteSpace: 'nowrap' }}>{displayLabel}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Box>
            <FieldLabel optional>{t('cibles.personne.firstName_label')}</FieldLabel>
            <TextField size="small" fullWidth value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t('cibles.personne.firstName_ph')}
              sx={inputSx} />
          </Box>

          <Box>
            <FieldLabel>{t('cibles.personne.lastName_label')}</FieldLabel>
            <TextField size="small" fullWidth value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              placeholder={t('cibles.personne.lastName_ph')}
              error={!lastName.trim() && lastName !== ''}
              sx={inputSx} />
          </Box>
        </Box>

        {/* Name forms — three sections */}
        {lastName.trim() && (
          <Box sx={{ pt: 2, borderTop: `1px solid ${K.line}`, display: 'flex', flexDirection: 'column', gap: 1.25 }}>

            {/* Section 1: Sans titre — always visible */}
            <Box>
              <Typography component="div" sx={{ ...microLabel, mb: 0.75 }}>
                {t('cibles.personne.no_title_group')}
              </Typography>
              <NameFormChips forms={sansTitreForms} activeNames={activeNames} onToggle={toggleName} color={cat.color} />
            </Box>

            {/* Section 2: Working-language title forms (collapsed by default) */}
            {titleDef && workingLangForms.length > 0 && (
              <Box sx={{ border: `1px solid ${K.line}`, borderRadius: '10px', overflow: 'hidden' }}>
                <Box
                  onClick={() => setShowTitleForms((p) => !p)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: 'pointer',
                    bgcolor: showTitleForms ? K.headBg : 'transparent',
                    '&:hover': { bgcolor: K.rowHover } }}
                >
                  <Typography component="span" sx={{ fontSize: '0.875rem', lineHeight: 1, flexShrink: 0 }}>{titleDef.emoji}</Typography>
                  <Typography sx={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: K.ink }}>
                    {t('cibles.personne.title_filter_label')}
                  </Typography>
                  {activeNames.size > sansTitreForms.filter(f => activeNames.has(f.form)).length && (
                    <Box sx={{ bgcolor: alpha(cat.color, 0.12), color: cat.color, fontWeight: 700, fontSize: '0.7rem', px: 0.75, py: 0.15, borderRadius: 0.75 }}>
                      {allForms.filter(f => f.lang !== '' && activeNames.has(f.form)).length}
                    </Box>
                  )}
                  <ChevronRightIcon sx={{ fontSize: 17, color: K.ink3, transform: showTitleForms ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                </Box>
                {showTitleForms && (
                  <Box sx={{ px: 1.5, pb: 1.25, pt: 0.5, borderTop: `1px solid ${K.line}` }}>
                    <Typography sx={{ display: 'block', mt: 0.5, mb: 0.75, fontSize: 11.5, color: K.ink3 }}>
                      {t('cibles.personne.title_filter_hint', { lang: PERSON_TITLE_LANG_LABELS[workingLang] ?? workingLang })}
                    </Typography>
                    <NameFormChips forms={workingLangForms} activeNames={activeNames} onToggle={toggleName} color={cat.color} />
                  </Box>
                )}
              </Box>
            )}

            {/* Section 3: Other languages (collapsed by default) */}
            {titleDef && otherLangForms.length > 0 && (
              <Box sx={{ border: `1px solid ${K.line}`, borderRadius: '10px', overflow: 'hidden' }}>
                <Box
                  onClick={() => setShowAllLangForms((p) => !p)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: 'pointer',
                    bgcolor: showAllLangForms ? K.headBg : 'transparent',
                    '&:hover': { bgcolor: K.rowHover } }}
                >
                  <Typography sx={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: K.ink }}>
                    {t('cibles.personne.all_langs_label')}
                  </Typography>
                  {allForms.filter(f => f.lang !== '' && f.lang !== workingLang && activeNames.has(f.form)).length > 0 && (
                    <Box sx={{ bgcolor: alpha(cat.color, 0.12), color: cat.color, fontWeight: 700, fontSize: '0.7rem', px: 0.75, py: 0.15, borderRadius: 0.75 }}>
                      {allForms.filter(f => f.lang !== '' && f.lang !== workingLang && activeNames.has(f.form)).length}
                    </Box>
                  )}
                  <ChevronRightIcon sx={{ fontSize: 17, color: K.ink3, transform: showAllLangForms ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                </Box>
                {showAllLangForms && (
                  <Box sx={{ px: 1.5, pb: 1.25, pt: 0.5, borderTop: `1px solid ${K.line}`, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {/* Group by language */}
                    {[...new Set(otherLangForms.map(f => f.lang))].sort().map(lang => (
                      <Box key={lang}>
                        <Typography component="div" sx={{ ...microLabel, mt: 0.5, mb: 0.5 }}>
                          {PERSON_TITLE_LANG_LABELS[lang] ?? lang}
                        </Typography>
                        <NameFormChips forms={otherLangForms.filter(f => f.lang === lang)} activeNames={activeNames} onToggle={toggleName} color={cat.color} />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Summary */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, px: 1.5, py: 1, borderRadius: '10px', bgcolor: alpha(cat.color, 0.06), color: alpha(cat.color, 0.9) }}>
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><circle cx="5.4" cy="10.6" r="3"/><path d="M7.5 8.5 13.2 2.8M11 5l1.7 1.7M9.4 6.6l1.3 1.3"/></svg>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
                <strong>{activeNames.size}</strong>{' '}
                {t('cibles.personne.names_summary', { total: allForms.length })}
              </Typography>
            </Box>
            <FieldHint>{t('cibles.personne.names_fold_hint')}</FieldHint>
          </Box>
        )}

        {/* Tags */}
        <Box>
          <FieldLabel optional hint={t('cibles.tags.hint')}>{t('cibles.tags.label')}</FieldLabel>
          <ChipListEditor
            values={tags}
            onChange={setTags}
            max={TARGET_LIMITS.maxTagsPerTarget}
            maxLength={TARGET_LIMITS.maxTagLength}
            placeholder={t('cibles.tags.placeholder')}
            suggestions={[...tagRegistry.values()]}
            disabled={saving}
            hex={cat.color}
          />
        </Box>

      </DialogContent>

      <DialogFoot>
        <Button variant="outlined" size="small" onClick={onClose} disabled={saving} sx={secondaryBtnSx}>
          {t('cibles.actions.cancel')}
        </Button>
        <Button variant="contained" size="small" disableElevation onClick={handleSave}
          disabled={saving || !lastName.trim()}
          sx={primaryBtnSx}>
          {saving ? <CircularProgress size={14} color="inherit" /> : (isEdit ? t('cibles.actions.save') : t('cibles.actions.add'))}
        </Button>
      </DialogFoot>
    </Dialog>
  );
}
